import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Stack, Chip, Divider } from '@mui/material';
import { serifFont } from '../../theme';

// Public, no-auth view of a shared stop. Hits the get-share edge function with a share_id slug.
// Mirrors the structure of /web/share/index.html in the mobile repo but rendered through MUI.

interface SharePayload {
  stop: { title: string; address: string | null; context: string | null; icon: string | null };
  photos: Array<{ image_url: string | null }>;
  chats: Array<{ chat_id: string; role: string; text: string; photo_urls: string[]; pinned: boolean }>;
  guide_items: Array<{
    masterpiece_id: string;
    rank: number;
    tier: string;
    kind: string;
    name: string;
    artist: string | null;
    date_or_period: string | null;
    instruction: string | null;
    why_it_matters: string | null;
    image_url: string | null;
  }>;
  outputs: Array<{ output_id: string; prompt_type: string; content: string }>;
}

function renderRich(text: string) {
  // Render **bold** and *italic* as MUI components inline.
  // Bold first to avoid the inner * matching italic.
  const out: React.ReactNode[] = [];
  const bold = text.split(/\*\*(.+?)\*\*/g);
  bold.forEach((bp, bi) => {
    if (bi % 2 === 1) {
      out.push(<strong key={`b${bi}`}>{bp}</strong>);
    } else {
      const it = bp.split(/\*(.+?)\*/g);
      it.forEach((part, ii) => {
        if (ii % 2 === 1) out.push(<em key={`b${bi}i${ii}`}>{part}</em>);
        else out.push(part);
      });
    }
  });
  return out;
}

export default function SharePage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [data, setData] = useState<SharePayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shareId) return;
    const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-share?id=${encodeURIComponent(shareId)}`;
    fetch(url, {
      headers: {
        apikey: process.env.REACT_APP_SUPABASE_ANON_KEY ?? '',
        Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY ?? ''}`,
      },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then(setData)
      .catch((err) => setError(err.message || 'Failed to load'));
  }, [shareId]);

  if (error) return <Box sx={{ p: 6, textAlign: 'center' }}><Typography color="error">{error}</Typography></Box>;
  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  const photos = data.photos.filter((p) => p.image_url);
  const mustSee = data.guide_items.filter((g) => g.tier !== 'important');
  const important = data.guide_items.filter((g) => g.tier === 'important');

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: 3, pb: 8 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontFamily: serifFont, fontStyle: 'italic' }}>
        Roamoir · shared place
      </Typography>
      <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 600, mt: 0.5 }}>
        {data.stop.icon ? `${data.stop.icon}  ` : ''}{data.stop.title}
      </Typography>
      {data.stop.address ? (
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>{data.stop.address}</Typography>
      ) : null}
      {data.stop.context ? (
        <Box sx={{ borderLeft: '3px solid', borderColor: 'secondary.main', pl: 2, my: 3 }}>
          <Typography sx={{ fontFamily: serifFont, fontStyle: 'italic' }}>{data.stop.context}</Typography>
        </Box>
      ) : null}

      {photos.length > 0 ? (
        <Box sx={{ mt: 4 }}>
          <Typography variant="overline" color="text.secondary">Photos · {photos.length}</Typography>
          <Box
            sx={{
              mt: 1,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
              gap: 1,
            }}
          >
            {photos.map((p, i) => (
              <Box
                key={i}
                sx={{ aspectRatio: '1 / 1', overflow: 'hidden', borderRadius: 1, bgcolor: 'divider' }}
              >
                <img
                  src={p.image_url!}
                  alt=""
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    imageOrientation: 'from-image',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {data.chats.length > 0 ? (
        <Box sx={{ mt: 4 }}>
          <Typography variant="overline" color="text.secondary">Conversations</Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {data.chats.map((c) => (
              <Box key={c.chat_id} sx={{ display: 'flex', gap: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                {c.photo_urls[0] ? (
                  <Box sx={{ flex: '0 0 96px' }}>
                    <img src={c.photo_urls[0]} alt="" loading="lazy" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6 }} />
                  </Box>
                ) : null}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    {c.role === 'assistant' ? 'Roamoir' : 'You'}
                    {c.pinned ? <Chip label="PINNED" size="small" color="secondary" sx={{ ml: 1, height: 18 }} /> : null}
                  </Typography>
                  <Typography sx={c.role === 'assistant' ? { fontFamily: serifFont, fontStyle: 'italic', whiteSpace: 'pre-wrap', mt: 0.5 } : { whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {renderRich(c.text)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}

      {data.guide_items.length > 0 ? (
        <Box sx={{ mt: 4 }}>
          <Typography variant="overline" color="text.secondary">Guide</Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {mustSee.map((g, i) => <GuideItem key={g.masterpiece_id} g={g} num={i + 1} />)}
            {important.length > 0 ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Also notable</Typography>
                {important.map((g) => <GuideItem key={g.masterpiece_id} g={g} num={null} />)}
              </>
            ) : null}
          </Stack>
        </Box>
      ) : null}

      {data.outputs.length > 0 ? (
        <Box sx={{ mt: 4 }}>
          <Typography variant="overline" color="text.secondary">Memoir</Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {data.outputs.map((o) => (
              <Box key={o.output_id} sx={{ bgcolor: 'primary.main', color: '#E8F4EC', p: 3, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, opacity: 0.6, display: 'block', mb: 1 }}>
                  {o.prompt_type.replace(/_/g, ' ')}
                </Typography>
                <Typography sx={{ fontFamily: serifFont, fontStyle: 'italic', lineHeight: 1.6 }}>{renderRich(o.content)}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

function GuideItem({ g, num }: { g: SharePayload['guide_items'][number]; num: number | null }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ flex: '0 0 32px', fontFamily: serifFont, fontStyle: 'italic', color: 'primary.main', fontSize: 22, fontWeight: 700 }}>
        {num ?? '·'}
      </Box>
      {g.image_url ? (
        <Box sx={{ flex: '0 0 120px' }}>
          <img src={g.image_url} alt="" loading="lazy" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6 }} />
        </Box>
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600 }}>{g.name}</Typography>
        {g.artist ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {g.artist}{g.date_or_period ? ` · ${g.date_or_period}` : ''}
          </Typography>
        ) : null}
        {g.instruction ? <Typography variant="body2" sx={{ mt: 0.5 }}>{g.instruction}</Typography> : null}
        {g.why_it_matters ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{g.why_it_matters}</Typography> : null}
      </Box>
    </Box>
  );
}
